import React, { useState, useEffect } from 'react';
import { CheckSquare, Calendar, User, Folder, AlertTriangle, MessageSquare, ExternalLink, X, Send } from 'lucide-react';
import { formatForDisplay, getRelativeTime, isOverdue } from '../../../utils/timezoneUtils';
import { useAuth } from '../../../context/AuthContext';

const ProjectNotesModal = ({ project, isOpen, onClose, onAddNote }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (isOpen && project) {
      fetchProjectNotes();
    }
  }, [isOpen, project]);

  const fetchProjectNotes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/project-tracker/projects/${project.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const projectData = await response.json();
        setNotes(projectData.notes || []);
      }
    } catch (error) {
      console.error('Error fetching project notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    setIsSubmitting(true);
    try {
      await onAddNote(project.id, newNote.trim());
      setNewNote('');
      // Refetch notes to get the updated list
      setTimeout(() => fetchProjectNotes(), 100);
    } catch (error) {
      console.error('Failed to add note:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg border border-slate-700 w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MessageSquare className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Project Notes</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Project Info */}
        <div className="p-4 bg-slate-700/30 border-b border-slate-700">
          <div className="flex items-center space-x-2">
            <Folder className="w-4 h-4 text-slate-400" />
            <span className="text-white font-medium">{project.name}</span>
            <span className="text-slate-400 text-sm">({notes.length} note{notes.length !== 1 ? 's' : ''})</span>
          </div>
        </div>

        {/* Notes List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-400">No notes yet for this project</p>
              <p className="text-slate-500 text-sm mt-1">Add the first note below to get started</p>
            </div>
          ) : (
            notes.map((note) => (
              <div key={note.id} className="bg-slate-700/30 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-slate-600 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-white">
                        {(note.created_by_name || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-slate-300 font-medium text-sm">{note.created_by_name || 'Unknown'}</span>
                  </div>
                  <span className="text-slate-500 text-xs">
                    {new Date(note.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <p className="text-slate-200 text-sm whitespace-pre-wrap">{note.content}</p>
              </div>
            ))
          )}
        </div>

        {/* Add Note Form */}
        <div className="p-4 border-t border-slate-700">
          <form onSubmit={handleAddNote} className="space-y-3">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note to this project..."
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
              rows="3"
              disabled={isSubmitting}
            />
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500">
                Press Shift+Enter for new line
              </div>
              <button
                type="submit"
                disabled={isSubmitting || !newNote.trim()}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Send className="w-4 h-4" />
                )}
                <span>{isSubmitting ? 'Adding...' : 'Add Note'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const TaskCard = ({ task, onCompleteTask, onOpenNotes, onJumpToProject, isCompleting, notesCount = 0 }) => {
  const getTimelineStatusColor = (status) => {
    switch (status) {
      case 'overdue': return 'text-red-400 bg-red-900/20 border-red-800';
      case 'active': return 'text-green-400 bg-green-900/20 border-green-800';
      case 'scheduled': return 'text-blue-400 bg-blue-900/20 border-blue-800';
      default: return 'text-slate-400 bg-slate-700/20 border-slate-600';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'border-l-red-500';
      case 'high': return 'border-l-orange-500';
      case 'medium': return 'border-l-blue-500';
      case 'low': return 'border-l-green-500';
      default: return 'border-l-slate-500';
    }
  };

  const handleCompleteClick = (e) => {
    e.stopPropagation();
    onCompleteTask(task.id);
  };

  const handleNotesClick = (e) => {
    e.stopPropagation();
    onOpenNotes(task.project_id, task.project_name);
  };

  const handleJumpToProjectClick = (e) => {
    e.stopPropagation();
    onJumpToProject(task.project_id);
  };

  return (
    <div
      className={`p-3 bg-slate-800 border border-slate-700 rounded-lg hover:border-slate-600 transition-all duration-200 border-l-4 ${getPriorityColor(task.project_priority)} h-full flex flex-col`}
    >
      {/* Status badge at top */}
      <div className="flex items-center justify-between mb-3">
        <div className={`px-2 py-1 rounded-full text-xs border ${getTimelineStatusColor(task.timeline_status)} flex items-center space-x-1`}>
          {task.timeline_status === 'overdue' && (
            <AlertTriangle className="w-3 h-3" />
          )}
          <span>
            {task.timeline_status === 'overdue' ? 'Overdue' :
             task.timeline_status === 'active' ? 'Active' :
             task.timeline_status === 'scheduled' ? 'Scheduled' : 'Unscheduled'}
          </span>
        </div>
        <div className="text-xs text-slate-500 capitalize">
          {task.project_priority} Priority
        </div>
      </div>

      {/* Task title - no truncation */}
      <h3 className="text-sm font-semibold text-white mb-2 leading-tight">
        {task.title}
      </h3>

      {/* Task description - allow wrapping */}
      {task.description && (
        <p className="text-slate-300 text-xs mb-3 leading-relaxed flex-1">
          {task.description}
        </p>
      )}

      {/* Due date info */}
      {task.end_datetime && (
        <div className="mb-3 text-xs text-slate-400">
          <div className="flex items-center space-x-1">
            <Calendar className="w-3 h-3" />
            <span>Due {formatForDisplay(task.end_datetime, 'short')}</span>
          </div>
          <div className="text-slate-500 mt-1">
            {getRelativeTime(task.end_datetime)}
          </div>
        </div>
      )}

      {/* Action buttons at bottom */}
      <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-700">
        <div className="flex items-center space-x-1">
          <button
            onClick={handleNotesClick}
            className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-600/20 rounded-lg transition-colors"
            title="View project notes"
          >
            <div className="relative">
              <MessageSquare className="w-4 h-4" />
              {notesCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                  {notesCount > 9 ? '9+' : notesCount}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={handleJumpToProjectClick}
            className="p-2 text-slate-400 hover:text-purple-400 hover:bg-purple-600/20 rounded-lg transition-colors"
            title="Jump to project"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
        <button
          onClick={handleCompleteClick}
          disabled={isCompleting}
          className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
            isCompleting
              ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
              : 'bg-green-600/20 text-green-400 hover:bg-green-600/30 hover:text-green-300 border border-green-600/30'
          }`}
          title="Mark as complete"
        >
          {isCompleting ? (
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
              <span>Completing...</span>
            </div>
          ) : (
            <div className="flex items-center space-x-1">
              <CheckSquare className="w-3 h-3" />
              <span>Complete</span>
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

const ProjectSection = ({ projectName, tasks, onCompleteTask, onOpenNotes, onJumpToProject, completingTaskIds, notesCountByProject = {} }) => {
  const overdueTasks = tasks.filter(task => task.timeline_status === 'overdue').length;
  const totalNotes = tasks.reduce((sum, task) => sum + (notesCountByProject[task.project_id] || 0), 0);

  return (
    <div className="mb-6">
      {/* Simple project header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <Folder className="w-5 h-5 text-blue-400" />
          <h2 className="text-lg font-bold text-white">{projectName}</h2>
          <div className="flex items-center space-x-2">
            <span className="px-2 py-1 bg-slate-600/60 text-slate-300 text-xs rounded-full">
              {tasks.length} task{tasks.length !== 1 ? 's' : ''}
            </span>
            {overdueTasks > 0 && (
              <span className="px-2 py-1 bg-red-900/40 text-red-300 text-xs rounded-full flex items-center space-x-1">
                <AlertTriangle className="w-3 h-3" />
                <span>{overdueTasks} overdue</span>
              </span>
            )}
            {totalNotes > 0 && (
              <span className="px-2 py-1 bg-blue-900/40 text-blue-300 text-xs rounded-full flex items-center space-x-1">
                <MessageSquare className="w-3 h-3" />
                <span>{totalNotes} note{totalNotes !== 1 ? 's' : ''}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Dense grid of task tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onCompleteTask={onCompleteTask}
            onOpenNotes={onOpenNotes}
            onJumpToProject={onJumpToProject}
            isCompleting={completingTaskIds.includes(task.id)}
            notesCount={notesCountByProject[task.project_id] || 0}
          />
        ))}
      </div>
    </div>
  );
};

const MyTasksList = ({ myTasks = [], onCompleteTask, onLoadMyTasks, onJumpToProject, onAddNote, loading = false }) => {
  const [completingTaskIds, setCompletingTaskIds] = useState([]);
  const [selectedProjectForNotes, setSelectedProjectForNotes] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesCountByProject, setNotesCountByProject] = useState({});
  const { currentUser } = useAuth();

  useEffect(() => {
    if (onLoadMyTasks) {
      onLoadMyTasks();
    }
  }, []);

  // Fetch notes count for each unique project
  useEffect(() => {
    const fetchNotesCount = async () => {
      const uniqueProjectIds = [...new Set(myTasks.map(task => task.project_id))];
      const counts = {};

      for (const projectId of uniqueProjectIds) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/project-tracker/projects/${projectId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const projectData = await response.json();
            counts[projectId] = (projectData.notes || []).length;
          } else {
            counts[projectId] = 0;
          }
        } catch (error) {
          console.error(`Error fetching notes count for project ${projectId}:`, error);
          counts[projectId] = 0;
        }
      }

      setNotesCountByProject(counts);
    };

    if (myTasks.length > 0) {
      fetchNotesCount();
    }
  }, [myTasks]);

  const handleCompleteTask = async (taskId) => {
    setCompletingTaskIds(prev => [...prev, taskId]);
    try {
      await onCompleteTask(taskId);
      // Task will be removed from the list via socket update or refetch
    } catch (error) {
      console.error('Failed to complete task:', error);
    } finally {
      setCompletingTaskIds(prev => prev.filter(id => id !== taskId));
    }
  };

  const handleOpenNotes = (projectId, projectName) => {
    setSelectedProjectForNotes({ id: projectId, name: projectName });
    setShowNotesModal(true);
  };

  const handleJumpToProject = (projectId) => {
    onJumpToProject(projectId);
  };

  const handleCloseNotesModal = () => {
    setShowNotesModal(false);
    setSelectedProjectForNotes(null);
  };

  // Group tasks by project (no filtering since we removed search)
  const groupedTasks = myTasks.reduce((groups, task) => {
    const projectName = task.project_name || 'Unknown Project';
    if (!groups[projectName]) {
      groups[projectName] = [];
    }
    groups[projectName].push(task);
    return groups;
  }, {});

  // Helper function to get priority weight for sorting
  const getPriorityWeight = (priority) => {
    switch (priority) {
      case 'critical': return 0;
      case 'high': return 1;
      case 'medium': return 2;
      case 'low': return 3;
      default: return 4;
    }
  };

  // Sort projects by priority only
  const sortedProjectNames = Object.keys(groupedTasks).sort((a, b) => {
    const aPriority = groupedTasks[a][0]?.project_priority;
    const bPriority = groupedTasks[b][0]?.project_priority;

    const aWeight = getPriorityWeight(aPriority);
    const bWeight = getPriorityWeight(bPriority);

    if (aWeight !== bWeight) {
      return aWeight - bWeight;
    }

    // Fallback to alphabetical for same priority
    return a.localeCompare(b);
  });

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading your tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-slate-950">
      {/* Minimal Header */}
      <div className="p-3 border-b border-slate-800">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-white">My Tasks</h1>
            <p className="text-slate-400 text-xs">
              {myTasks.length} active task{myTasks.length !== 1 ? 's' : ''} assigned to you
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <User className="w-4 h-4 text-blue-400" />
            <span className="text-slate-300 text-xs">{currentUser?.full_name || currentUser?.username}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3">
        {myTasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <CheckSquare className="w-16 h-16 text-slate-600 mb-4" />
            <h2 className="text-xl font-semibold text-slate-400 mb-2">No active tasks</h2>
            <p className="text-slate-500 max-w-md">
              You don't have any active tasks assigned to you. Check back later or ask your project manager to assign you some tasks.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedProjectNames.map((projectName) => (
              <ProjectSection
                key={projectName}
                projectName={projectName}
                tasks={groupedTasks[projectName].sort((a, b) => {
                  // Sort tasks within project: overdue first, then by due date
                  if (a.timeline_status === 'overdue' && b.timeline_status !== 'overdue') return -1;
                  if (a.timeline_status !== 'overdue' && b.timeline_status === 'overdue') return 1;

                  if (a.end_datetime && b.end_datetime) {
                    return new Date(a.end_datetime) - new Date(b.end_datetime);
                  }

                  return new Date(b.created_at) - new Date(a.created_at);
                })}
                onCompleteTask={handleCompleteTask}
                onOpenNotes={handleOpenNotes}
                onJumpToProject={handleJumpToProject}
                completingTaskIds={completingTaskIds}
                notesCountByProject={notesCountByProject}
              />
            ))}
          </div>
        )}
      </div>

      {/* Project Notes Modal */}
      {showNotesModal && selectedProjectForNotes && (
        <ProjectNotesModal
          project={selectedProjectForNotes}
          isOpen={showNotesModal}
          onClose={handleCloseNotesModal}
          onAddNote={onAddNote}
        />
      )}
    </div>
  );
};

export default MyTasksList;